import {
	type InfiniteData,
	type UndefinedInitialDataInfiniteOptions,
	type UseInfiniteQueryResult,
	useInfiniteQuery,
} from "@tanstack/react-query";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import {
	useDocumentsFeatureOptional,
	useRemindersRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import {
	getNotificationCenterRecentV2,
	type RecentTimelineItemTypeFilterEnum,
	type TimelineResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponseQuery } from "web_myplann/Infrastructure/Api/ApiUtils";
import { filterTimelineItemsByRights } from "web_myplann/NotificationCenter/Utils/filterTimelineItemsByRights";
import {
	type DocumentsOnlyFilter,
	resolveDocumentsOnlyFilter,
} from "web_myplann/NotificationCenter/Utils/resolveDocumentsOnlyFilter";

const STALE_TIME_MS = 60_000;
const PAGE_LIMIT = 20;

type Key = ReturnType<typeof NotificationCenterKeys.recent>;

interface RecentTimelineParams {
	type: RecentTimelineItemTypeFilterEnum;
	documentsOnly?: boolean;
}

const getRecentInfiniteOptions = (
	type: RecentTimelineItemTypeFilterEnum,
	hasRemindersRight: boolean,
	hasDocumentsFeature: boolean,
	{ documentsOnly, subcategories }: DocumentsOnlyFilter,
): UndefinedInitialDataInfiniteOptions<
	TimelineResponseDTO,
	Error,
	InfiniteData<TimelineResponseDTO, number>,
	Key,
	number
> => {
	return {
		queryKey: NotificationCenterKeys.recent(type, subcategories),
		queryFn: (ctx) =>
			processResponseQuery(() =>
				getNotificationCenterRecentV2(
					type,
					undefined,
					subcategories,
					undefined,
					undefined,
					PAGE_LIMIT,
					ctx.pageParam,
				),
			)(ctx),
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage.meta.hasMore) {
				return null;
			}
			return allPages.length * PAGE_LIMIT;
		},
		initialPageParam: 0,
		staleTime: STALE_TIME_MS,
		select: (data) => ({
			...data,
			pages: data.pages.map((page) =>
				filterTimelineItemsByRights(page, {
					hasRemindersRight,
					hasDocumentsFeature,
					documentsOnly,
				}),
			),
		}),
	};
};

export const useRecentTimelineInfiniteQuery = (
	params: RecentTimelineParams,
	options?: { enabled?: boolean },
): UseInfiniteQueryResult<InfiniteData<TimelineResponseDTO, number>, Error> => {
	const hasRemindersRight = useRemindersRightOptional();
	const hasDocumentsFeature = useDocumentsFeatureOptional();

	return useInfiniteQuery({
		...getRecentInfiniteOptions(
			params.type,
			hasRemindersRight,
			hasDocumentsFeature,
			resolveDocumentsOnlyFilter(params.documentsOnly, hasDocumentsFeature),
		),
		enabled: options?.enabled ?? true,
	});
};
